// Sprint 7 — Cobros reporting

import type { ReactNode } from "react";
import { css } from "styled-system/css";
import { motion } from "motion/react";
import { Calendar, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";

export type PaymentsTableFilters = {
  completed: boolean;
  pending: boolean;
  unpaid: boolean;
  refunded: boolean;
  cancelled: boolean;
};

export const DEFAULT_PAYMENTS_TABLE_FILTERS: PaymentsTableFilters = {
  completed: true,
  pending: true,
  unpaid: true,
  refunded: true,
  cancelled: true,
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  filters: PaymentsTableFilters;
  onFiltersChange: (next: PaymentsTableFilters) => void;
  headerExtra?: ReactNode;
};

const chkLabel = css({
  display: "flex",
  alignItems: "center",
  gap: "2",
  fontSize: "sm",
  color: "#334155",
  cursor: "pointer",
  userSelect: "none",
});

export function PaymentsFiltersPanel({
  open,
  onOpenChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  filters,
  onFiltersChange,
  headerExtra,
}: Props) {
  function toggle<K extends keyof PaymentsTableFilters>(key: K) {
    onFiltersChange({ ...filters, [key]: !filters[key] });
  }

  return (
    <div className={css({ mb: "4" })}>
      <div className={css({ display: "flex", flexWrap: "wrap", gap: "2", alignItems: "center", mb: "2" })}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => onOpenChange(!open)}
          className={css({ display: "inline-flex", alignItems: "center", gap: "2" })}
        >
          <SlidersHorizontal size={18} strokeWidth={2} aria-hidden />
          Filtros
        </Button>
        {headerExtra}
      </div>

      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0 }}
        transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
        style={{ overflow: "hidden" }}
      >
        <div
          className={css({
            display: "flex",
            flexDir: "column",
            gap: "4",
            p: "4",
            rounded: "lg",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            bg: "#f8fafc",
          })}
        >
          <div className={css({ display: "flex", flexWrap: "wrap", gap: "4", alignItems: "flex-end" })}>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "xs", color: "#64748b", display: "flex", alignItems: "center", gap: "1" })}>
                <Calendar size={14} strokeWidth={2} aria-hidden />
                Desde
              </span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => onDateFromChange(e.target.value)}
                className={css({
                  padding: "8px 10px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  fontSize: "sm",
                  bg: "white",
                })}
              />
            </label>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "xs", color: "#64748b", display: "flex", alignItems: "center", gap: "1" })}>
                <Calendar size={14} strokeWidth={2} aria-hidden />
                Hasta
              </span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => onDateToChange(e.target.value)}
                className={css({
                  padding: "8px 10px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  fontSize: "sm",
                  bg: "white",
                })}
              />
            </label>
          </div>

          <fieldset className={css({ border: "none", p: 0, m: 0 })}>
            <legend className={css({ fontSize: "xs", fontWeight: "semibold", color: "#64748b", mb: "2" })}>Estado de pago / turno</legend>
            <div className={css({ display: "flex", flexWrap: "wrap", gap: "4" })}>
              <label className={chkLabel}>
                <input type="checkbox" checked={filters.completed} onChange={() => toggle("completed")} />
                Pagado (total)
              </label>
              <label className={chkLabel}>
                <input type="checkbox" checked={filters.pending} onChange={() => toggle("pending")} />
                Parcial / seña
              </label>
              <label className={chkLabel}>
                <input type="checkbox" checked={filters.unpaid} onChange={() => toggle("unpaid")} />
                Sin pagar
              </label>
              <label className={chkLabel}>
                <input type="checkbox" checked={filters.refunded} onChange={() => toggle("refunded")} />
                Reembolsado
              </label>
              <label className={chkLabel}>
                <input type="checkbox" checked={filters.cancelled} onChange={() => toggle("cancelled")} />
                Cancelado
              </label>
            </div>
          </fieldset>
        </div>
      </motion.div>
    </div>
  );
}
